/**
 * Inputs: a pasted Google Maps URL. Outputs: name, address and coordinates when the link itself contains them.
 * Does not fetch the web. Short links still need a host-allowlisted resolver.
 */

export const MAPS_URL_COORD_SOURCE = "Owner-pasted Google Maps URL (pin not human-verified)";

export type MapsCoordinates = { lat: number; lng: number };

export type ParsedMapsUrl = {
  ok: true;
  canonical: string;
  name: string | null;
  address: string | null;
  coordinates: MapsCoordinates | null;
  isShortLink: boolean;
  needsResolve: boolean;
};

export type MapsUrlParseFailure = {
  ok: false;
  error: string;
};

const SHORT_HOSTS = new Set(["maps.app.goo.gl", "goo.gl", "www.goo.gl"]);

export function decodeMapsText(raw: string): string {
  const plusAsSpace = raw.replace(/\+/g, " ");
  try {
    return decodeURIComponent(plusAsSpace).replace(/\s+/g, " ").trim();
  } catch {
    return plusAsSpace.replace(/\s+/g, " ").trim();
  }
}

export function isFiniteCoord(lat: number, lng: number): boolean {
  return Number.isFinite(lat) && Number.isFinite(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
}

export function looksLikeCoordinates(value: string): MapsCoordinates | null {
  const match = value.trim().match(/^(-?\d{1,2}(?:\.\d+)?)\s*,\s*(-?\d{1,3}(?:\.\d+)?)$/);
  if (!match) return null;
  const lat = Number(match[1]);
  const lng = Number(match[2]);
  return isFiniteCoord(lat, lng) ? { lat, lng } : null;
}

export function looksLikePlusCode(value: string): boolean {
  return /^[2-9CFGHJMPQRVWX]{4,}\+[2-9CFGHJMPQRVWX]{2,}\b/i.test(value.trim());
}

export function looksLikeAddress(value: string): boolean {
  const text = value.trim();
  if (!text || looksLikeCoordinates(text)) return false;
  if (looksLikePlusCode(text)) return true;
  if (/\b\d{5}\b/.test(text)) return true;
  const street = /\b(rd|road|st|street|ave|avenue|soi|moo|lane|ln|drive|dr|thanon|ถนน|road|district|sub-district|tambon|amphoe|changwat|province)\b/i;
  if (/\d/.test(text) && street.test(text)) return true;
  if ((text.match(/,/g) ?? []).length >= 2) return true;
  if (/^\d+[/-]?\d*\s+\S+/.test(text)) return true;
  return false;
}

export function isGoogleMapsHostname(hostname: string): boolean {
  const host = hostname.toLowerCase();
  if (SHORT_HOSTS.has(host)) return true;
  return /^(www\.)?(maps\.)?google\.((com)(\.[a-z]{2})?|co\.[a-z]{2}|[a-z]{2})$/.test(host);
}

export function isAllowedMapsUrl(value: string): boolean {
  try {
    const url = new URL(value.trim());
    if (url.protocol !== "https:" && url.protocol !== "http:") return false;
    if (url.username || url.password) return false;
    if (!isGoogleMapsHostname(url.hostname)) return false;
    const host = url.hostname.toLowerCase();
    if (SHORT_HOSTS.has(host)) {
      return host === "maps.app.goo.gl" || url.pathname.startsWith("/maps");
    }
    if (host === "maps.google.com" || host.startsWith("maps.google.")) return true;
    return url.pathname.toLowerCase().includes("/maps");
  } catch {
    return false;
  }
}

function lastCoordPair(text: string, pattern: RegExp): MapsCoordinates | null {
  let found: MapsCoordinates | null = null;
  const global = new RegExp(pattern.source, pattern.flags.includes("g") ? pattern.flags : `${pattern.flags}g`);
  for (const match of text.matchAll(global)) {
    const lat = Number(match[1]);
    const lng = Number(match[2]);
    if (isFiniteCoord(lat, lng)) found = { lat, lng };
  }
  return found;
}

function splitNameAndAddress(label: string): { name: string | null; address: string | null } {
  const text = label.trim();
  if (!text) return { name: null, address: null };
  if (looksLikeCoordinates(text)) return { name: null, address: null };
  if (looksLikeAddress(text)) {
    const comma = text.split(",").map((part) => part.trim()).filter(Boolean);
    if (comma.length >= 2 && !looksLikeAddress(comma[0]) && !/\d/.test(comma[0])) {
      return { name: comma[0], address: comma.slice(1).join(", ") };
    }
    return { name: null, address: text };
  }
  const comma = text.split(",").map((part) => part.trim()).filter(Boolean);
  if (comma.length >= 2 && looksLikeAddress(comma.slice(1).join(", "))) {
    return { name: comma[0], address: comma.slice(1).join(", ") };
  }
  return { name: text, address: null };
}

function placeLabelFromPath(pathname: string): string | null {
  const match = pathname.match(/\/(?:place|search|dir)\/([^/@]+)/i);
  if (!match) return null;
  const raw = match[1];
  if (!raw || raw.startsWith("data=")) return null;
  const decoded = decodeMapsText(raw);
  return decoded && !looksLikeCoordinates(decoded) ? decoded : null;
}

function queryLabel(url: URL): { text: string | null; coordinates: MapsCoordinates | null } {
  const keys = ["query", "q", "destination", "daddr"];
  for (const key of keys) {
    const value = url.searchParams.get(key);
    if (!value) continue;
    const decoded = decodeMapsText(value);
    const coords = looksLikeCoordinates(decoded);
    if (coords) return { text: null, coordinates: coords };
    if (decoded) return { text: decoded, coordinates: null };
  }
  return { text: null, coordinates: null };
}

export function parseMapsUrl(raw: string): ParsedMapsUrl | MapsUrlParseFailure {
  const trimmed = raw.trim();
  if (!trimmed) return { ok: false, error: "Paste a Google Maps link." };
  if (!isAllowedMapsUrl(trimmed)) {
    return { ok: false, error: "That is not a Google Maps link we can read." };
  }
  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return { ok: false, error: "That is not a Google Maps link we can read." };
  }
  const host = url.hostname.toLowerCase();
  const isShortLink = SHORT_HOSTS.has(host);
  const haystack = `${url.pathname}${url.search}${url.hash}`;
  const fromData = lastCoordPair(haystack, /!3d(-?\d+\.?\d*)!4d(-?\d+\.?\d*)/);
  const fromAt = lastCoordPair(haystack, /@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
  const fromQuery = queryLabel(url);
  const coordinates = fromData ?? fromAt ?? fromQuery.coordinates;
  const pathLabel = placeLabelFromPath(url.pathname);
  const label = pathLabel ?? fromQuery.text;
  const split = label ? splitNameAndAddress(label) : { name: null, address: null };
  return {
    ok: true,
    canonical: trimmed,
    name: split.name,
    address: split.address,
    coordinates,
    isShortLink,
    needsResolve: isShortLink || (!split.address && !split.name && !coordinates),
  };
}

export type MapsPlaceHint = {
  mapsUrl: string;
  name: string | null;
  address: string | null;
  thaiName: string | null;
  coordinates: MapsCoordinates | null;
  addressSource: "maps_url" | "reverse_geocode" | null;
  note: string | null;
};

export function hintFromParsedMapsUrl(parsed: ParsedMapsUrl, originalUrl: string): MapsPlaceHint {
  return {
    mapsUrl: originalUrl.trim(),
    name: parsed.name,
    address: parsed.address,
    thaiName: null,
    coordinates: parsed.coordinates,
    addressSource: parsed.address ? "maps_url" : null,
    note: parsed.address
      ? "Address taken from the map link."
      : parsed.isShortLink
        ? "Reading the short map link…"
        : parsed.coordinates
          ? "Pin found. Looking up a street address…"
          : "Map link saved. Add a street address if the driver card needs one.",
  };
}

export function fillIfEmpty(current: string, next: string | null | undefined): string {
  if (current.trim()) return current;
  return next?.trim() ?? "";
}
